import React, { useState, useEffect, lazy, Suspense } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Menu, X, UserCircle, ChevronDown } from 'lucide-react';
import { useBusinessHours, formatBusinessHoursSummary } from '@/hooks/useBusinessHours';

const NotificationBell = lazy(() => import('@/components/NotificationBell'));

// KX Logo Component - Using Dubai Bold font
export const KXLogo = ({ variant = 'dark', className = '', size = 'default' }) => {
  const textColor = variant === 'light' ? '#ffffff' : '#1a1a1a';
  
  // Size configurations
  const sizeConfig = {
    small: { fontSize: '28px', letterSpacing: '-2px' },
    default: { fontSize: '36px', letterSpacing: '-2px' },
    large: { fontSize: '48px', letterSpacing: '-3px' },
    footer: { fontSize: '42px', letterSpacing: '-2px' }
  };
  
  const { fontSize, letterSpacing } = sizeConfig[size] || sizeConfig.default;
  
  return (
    <span 
      className={`select-none cursor-pointer transition-all duration-300 ${className}`}
      style={{
        fontFamily: "'Dubai', sans-serif",
        fontWeight: 700,
        fontSize: fontSize,
        letterSpacing: letterSpacing,
        color: textColor,
        lineHeight: 1
      }}
      aria-label="KX Logo"
    >
      KX
    </span>
  );
};

// Full KX Logo with text - "KITCHN XPRESS" and tagline
export const KXLogoFull = ({ variant = 'dark', className = '' }) => {
  const fillColor = variant === 'light' ? '#ffffff' : '#1a1a1a';
  const subtitleColor = variant === 'light' ? 'rgba(255,255,255,0.7)' : '#666666';
  
  return (
    <div className={`flex flex-col items-center select-none cursor-pointer ${className}`}>
      <KXLogo variant={variant} size="large" />
      <span 
        style={{ 
          fontFamily: "'Dubai', sans-serif",
          fontSize: '14px',
          letterSpacing: '0.2em',
          color: fillColor,
          marginTop: '8px'
        }}
      >
        KITCHN XPRESS
      </span>
      <div 
        style={{ 
          width: '100px', 
          height: '1px', 
          backgroundColor: fillColor,
          opacity: 0.3,
          margin: '6px 0'
        }} 
      />
      <span 
        style={{ 
          fontFamily: "'Dubai', sans-serif",
          fontSize: '10px',
          letterSpacing: '0.15em',
          color: subtitleColor
        }}
      >
        REIMAGINE YOUR SPACE
      </span>
    </div>
  );
};

// Shared Navigation Component for consumer pages
const ConsumerNav = ({ 
  transparent = false, 
  activeLink = '' 
}) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [learnOpen, setLearnOpen] = useState(false); // mobile sub-menu for "How It Works"
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Check customer login status
  const [isCustomerLoggedIn, setIsCustomerLoggedIn] = useState(false);
  useEffect(() => {
    const token = localStorage.getItem('customerToken');
    setIsCustomerLoggedIn(!!token);
    const onStorage = () => setIsCustomerLoggedIn(!!localStorage.getItem('customerToken'));
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // Determine logo variant based on scroll and transparent prop
  const logoVariant = transparent && !scrolled ? 'light' : 'dark';
  const linkClass = transparent && !scrolled ? 'nav-link-light' : 'nav-link';

  const navLinks = [
    { href: '/pricing', label: 'Pricing' },
    { href: '/#testimonials', label: 'Reviews' },
    { href: '/#our-work', label: 'Gallery' },
    { href: '/contact', label: 'Contact' },
  ];

  // Sub-links under the "How It Works" dropdown
  // href starting with /# are anchor links — rendered as <a> for reliable scroll behaviour
  const learnSubLinks = [
    { href: '/what-is-cabinet-refacing', label: 'What is Cabinet Refacing' },
    { href: '/#process', label: 'How It Works' },
    { href: '/faq', label: 'FAQ' },
  ];

  const isActive = (href) => {
    if (href.startsWith('/#')) return false;
    return location.pathname === href;
  };

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
      scrolled ? 'nav-glass shadow-sm' : (transparent ? 'bg-transparent' : 'bg-white')
    }`}>
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex items-center justify-between h-20 lg:h-24">
          <Link to="/" className="flex items-center" data-testid="nav-logo">
            <KXLogo variant={logoVariant} />
          </Link>
          
          <div className="hidden lg:flex items-center gap-10">
            {/* "How It Works" dropdown — non-navigable parent with 3 sub-links */}
            <div className="relative group" data-testid="how-it-works-dropdown">
              <button
                type="button"
                className={`${linkClass} flex items-center gap-1 cursor-default select-none`}
                tabIndex={0}
                aria-haspopup="true"
                aria-expanded="false"
              >
                How It Works
                <ChevronDown className="h-3.5 w-3.5 opacity-60 transition-transform duration-200 group-hover:rotate-180" />
              </button>

              {/* Dropdown panel */}
              <div className="absolute top-full left-1/2 -translate-x-1/2 pt-4 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 pointer-events-none group-hover:pointer-events-auto z-50">
                <div className="bg-[#1a1a1a] border border-[#C5A059]/25 rounded-xl shadow-2xl py-2 min-w-[220px] overflow-hidden"
                  style={{ borderTop: '2px solid #C5A059' }}
                >
                  {learnSubLinks.map((sub, i) => {
                    const cls = `block px-5 py-3.5 text-sm text-white/75 hover:text-[#C5A059] hover:bg-white/5 transition-colors ${
                      i < learnSubLinks.length - 1 ? 'border-b border-white/5' : ''
                    }`;
                    return sub.href.startsWith('/#') ? (
                      <a
                        key={sub.href}
                        href={sub.href}
                        className={cls}
                        data-testid={`dropdown-${sub.label.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        {sub.label}
                      </a>
                    ) : (
                      <Link
                        key={sub.href}
                        to={sub.href}
                        className={cls}
                        data-testid={`dropdown-${sub.label.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        {sub.label}
                      </Link>
                    );
                  })}
                </div>
              </div>
            </div>

            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`${linkClass} ${isActive(link.href) || activeLink === link.label ? 'text-brass' : ''}`}
              >
                {link.label}
              </Link>
            ))}
            <div className="flex items-center gap-3">
              {isCustomerLoggedIn && (
                <Suspense fallback={null}>
                  <NotificationBell tokenKey="customerToken" variant={logoVariant === 'light' ? 'light' : 'dark'} />
                </Suspense>
              )}
              <Link to={isCustomerLoggedIn ? "/account/dashboard" : "/sign-in"} data-testid="nav-signin-btn">
                <Button className={`px-5 py-2.5 rounded-md font-medium text-sm transition-all bg-transparent ${
                  logoVariant === 'light'
                    ? 'border border-white/40 text-white hover:bg-white/10 hover:border-white/70'
                    : 'border border-gray-300 text-foreground hover:border-brass hover:text-brass'
                }`}>
                  {isCustomerLoggedIn ? (
                    <><UserCircle className="mr-1.5 h-4 w-4" />My Account</>
                  ) : 'Sign In'}
                </Button>
              </Link>
              <a href="/#path-selection" data-testid="nav-start-btn">
                <Button className="btn-accent">Start Your Design</Button>
              </a>
            </div>
          </div>

          <button 
            className={`lg:hidden p-2.5 rounded-lg transition-colors ${
              logoVariant === 'light' ? 'text-white hover:bg-white/10' : 'text-kx-charcoal hover:bg-gray-100'
            }`}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-white border-t border-gray-100 shadow-lg">
          <div className="px-6 py-8 space-y-5">
            {/* How It Works expandable group */}
            <div>
              <button
                type="button"
                className="flex items-center justify-between w-full nav-link py-2.5 text-base"
                onClick={() => setLearnOpen(!learnOpen)}
                data-testid="mobile-how-it-works-toggle"
              >
                How It Works
                <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${learnOpen ? 'rotate-180' : ''}`} />
              </button>
              {learnOpen && (
                <div className="mt-1 ml-4 space-y-3 border-l-2 border-[#C5A059]/30 pl-4">
                  {learnSubLinks.map((sub) => {
                    const cls = "block text-sm text-muted-foreground hover:text-[#C5A059] py-1.5 transition-colors";
                    return sub.href.startsWith('/#') ? (
                      <a
                        key={sub.href}
                        href={sub.href}
                        className={cls}
                        onClick={() => { setMobileMenuOpen(false); setLearnOpen(false); }}
                        data-testid={`mobile-${sub.label.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        {sub.label}
                      </a>
                    ) : (
                      <Link
                        key={sub.href}
                        to={sub.href}
                        className={cls}
                        onClick={() => { setMobileMenuOpen(false); setLearnOpen(false); }}
                        data-testid={`mobile-${sub.label.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        {sub.label}
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>

            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`block nav-link py-2.5 text-base ${isActive(link.href) || activeLink === link.label ? 'text-brass' : ''}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                {link.label}
              </Link>
            ))}
            <Link to={isCustomerLoggedIn ? "/account/dashboard" : "/sign-in"} className="block nav-link py-2.5 text-base" onClick={() => setMobileMenuOpen(false)}>
              {isCustomerLoggedIn ? 'My Account' : 'Sign In'}
            </Link>
            <a href="/#path-selection" onClick={() => setMobileMenuOpen(false)}>
              <Button className="btn-accent w-full mt-4">Start Your Design</Button>
            </a>
          </div>
        </div>
      )}
    </nav>
  );
};

// Shared Footer Component for consumer pages
export const ConsumerFooter = () => {
  const { hours } = useBusinessHours();
  const hoursSummary = formatBusinessHoursSummary(hours);

  return (
    <footer className="py-16 lg:py-20 bg-charcoal">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid md:grid-cols-4 gap-12 lg:gap-16 mb-14">
          <div>
            <div className="mb-3">
              <KXLogo variant="light" size="footer" />
            </div>
            <p className="text-white/70 text-sm leading-relaxed">
              <span className="block">REIMAGINE your space</span>
              <span className="block">DEFINE it with KX</span>
            </p>
          </div>
          <div>
            <h4 className="text-white font-semibold mb-5">Quick Links</h4>
            <div className="space-y-3.5">
              <a href="/#path-selection" className="block text-white/60 hover:text-brass transition-colors text-sm">Kitchen Visualizer</a>
              <Link to="/pricing" className="block text-white/60 hover:text-brass transition-colors text-sm">Pricing Guide</Link>
              <Link to="/gallery" className="block text-white/60 hover:text-brass transition-colors text-sm">Our Work</Link>
              <a href="/#process" className="block text-white/60 hover:text-brass transition-colors text-sm">How It Works</a>
              <Link to="/#testimonials" className="block text-white/60 hover:text-brass transition-colors text-sm">Reviews</Link>
              <Link to="/contact" className="block text-white/60 hover:text-brass transition-colors text-sm">Contact Us</Link>
            </div>
          </div>
          <div>
            <h4 className="text-white font-semibold mb-5">Discover KX</h4>
            <div className="space-y-3.5">
              <Link to="/careers" className="block text-white/60 hover:text-brass transition-colors text-sm">Career Opportunities</Link>
              <Link to="/faq" className="block text-white/60 hover:text-brass transition-colors text-sm">FAQ's</Link>
              <Link to="/history-of-kx" className="block text-white/60 hover:text-brass transition-colors text-sm">History of KX</Link>
            </div>
          </div>
          <div>
            <h4 className="text-white font-semibold mb-5">Contact</h4>
            <div className="space-y-3.5">
              <p className="text-white/60 text-sm">1-800-KXPRESS</p>
              <p className="text-white/60 text-sm">info@kitchnxpress.ca</p>
              <p className="text-white/60 text-sm" data-testid="footer-business-hours">{hoursSummary}</p>
            </div>
          </div>
        </div>
        
        <div className="border-t border-white/10 pt-10 flex flex-col md:flex-row items-center justify-between gap-6">
          <p className="text-white/40 text-sm">
            © {new Date().getFullYear()} KX Kitchens. All rights reserved.
          </p>
          <div className="flex items-center gap-8 text-sm">
            <Link to="/privacy-policy" className="text-white/40 hover:text-white transition-colors">Privacy Policy</Link>
            <Link to="/terms-of-service" className="text-white/40 hover:text-white transition-colors">Terms of Service</Link>
            <Link to="/staff/login" className="text-white/40 hover:text-white transition-colors">Admin</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default ConsumerNav;
